import styled from 'styled-components';
import type { FleetModel, FleetModelPrcBrief, FleetModelPrcUpcoming } from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { IconArrowDown, IconArrowUp } from '@shared/ui/icons';
import {
  FleetStatusChip,
  PrcStaleChip,
  RUB_NO_VAT,
  ScrollRegion,
  fleetLeading,
  fleetType,
  formatCount,
  formatIsoDate,
  formatMoney,
  rateUnit,
  typo,
  type FleetModelGroup,
  type FleetSortKey,
  type SortDirection,
} from '@entities/fleet';

/**
 * Список моделей. Широкий контейнер — таблица; узкий (телефон, свёрнутая
 * колонка) — строки-карточки: название и стоимость, ниже категория, единицы,
 * топливо, собственник и механизатор. Переключение — container query, без JS.
 */

const NARROW = '640px';

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  thead th {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: left;
    color: ${({ theme }) => theme.colors.text.secondary};
    vertical-align: bottom;
  }

  thead th.num {
    text-align: right;
  }

  td {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
    color: ${({ theme }) => theme.colors.text.primary};
    transition: background ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut};
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.c-name {
    min-width: 220px;
  }

  /* 120, а не 140 px: с колонкой ПРЦ плоский список (с категорией) должен
     помещаться в 1280 px при раскрытом меню. Текст здесь и так переносится. */
  td.c-owner,
  td.c-position {
    min-width: 120px;
    max-width: 240px;
    overflow-wrap: anywhere;
  }

  td.empty {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  /* «не утверждена» — словами и вторым цветом: читается и без цвета. Подписи
     под ценой переносятся: колонка не шире самой цены. */
  td.c-prc.none {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    white-space: normal;
  }

  td.c-prc > span {
    white-space: normal;
  }

  /* Цена ещё не действует — вторым цветом, подпись «вступит с …». */
  td.c-prc.future {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  tr.row {
    cursor: pointer;
  }

  tr.row:hover > td,
  tr.row:focus-within > td {
    background: ${({ theme }) => theme.colors.surface};
  }

  tr.group > th {
    padding: ${({ theme }) => `${theme.spacing(6)} ${theme.spacing(3)} ${theme.spacing(2)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    text-align: left;
    font-weight: 400;
  }

  tbody:first-of-type tr.group > th {
    padding-top: ${({ theme }) => theme.spacing(4)};
  }

  .narrow-only {
    display: none;
  }

  @container fleet (max-width: ${NARROW}) {
    &,
    & > tbody,
    & tr,
    & th,
    & td {
      display: block;
    }

    & > thead,
    .wide-only {
      display: none;
    }

    .narrow-only {
      display: block;
    }

    tr.row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      column-gap: ${({ theme }) => theme.spacing(3)};
      row-gap: ${({ theme }) => theme.spacing(1)};
      padding: ${({ theme }) => `${theme.spacing(3)} 0`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    tr.row > td {
      padding: 0;
      border: none;
      min-width: 0;
      max-width: none;
      background: none;
    }

    tr.row:hover,
    tr.row:focus-within {
      background: ${({ theme }) => theme.colors.surface};
    }

    td.c-name {
      grid-column: 1;
      grid-row: 1;
    }

    td.c-cost {
      grid-column: 2;
      grid-row: 1;
    }

    td.c-meta {
      grid-column: 1 / -1;
      grid-row: 2;
    }

    td.c-people {
      grid-column: 1 / -1;
      grid-row: 3;
    }

    td.c-prc-line {
      grid-column: 1 / -1;
      grid-row: 4;
    }

    tr.group > th {
      padding: ${({ theme }) => `${theme.spacing(5)} 0 ${theme.spacing(2)}`};
    }
  }
`;

const GroupTitle = styled.h2`
  display: inline;
  margin: 0;
  font-size: ${fleetType.small};
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const GroupMeta = styled.span`
  margin-left: ${({ theme }) => theme.spacing(2)};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const NameButton = styled.button`
  display: block;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fleetType.base};
  font-weight: 500;
  line-height: ${fleetLeading.tight};
  text-align: left;
  text-wrap: pretty;
  overflow-wrap: anywhere;
  cursor: pointer;
  transition: color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  tr.row:hover & {
    color: ${({ theme }) => theme.colors.primary};
  }

  tr[data-archived='true'] & {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Chips = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(1.5)};

  &:empty {
    display: none;
  }
`;

const Unit = styled.span`
  display: block;
  font-size: ${fleetType.micro};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const Meta = styled.span`
  display: block;
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > svg {
    flex: none;
  }
`;

interface Column {
  key: string;
  label: string;
  num?: boolean;
  sort?: FleetSortKey;
}

const CATEGORY_COLUMN: Column = { key: 'category', label: 'Категория', sort: 'category' };

function columns(flat: boolean): Column[] {
  return [
    { key: 'name', label: 'Наименование', sort: 'name' },
    ...(flat ? [CATEGORY_COLUMN] : []),
    { key: 'units', label: 'Единиц', num: true, sort: 'units' },
    { key: 'owner', label: 'Собственник' },
    { key: 'position', label: 'Должность механизатора' },
    { key: 'fuel', label: 'Топливо' },
    { key: 'cost', label: `Стоимость, ${RUB_NO_VAT}`, num: true, sort: 'cost' },
    { key: 'prc', label: `ПРЦ без НДС, ${rateUnit('machine_hour')}`, num: true, sort: 'prc' },
  ];
}

/** «с 01.10.2026»: дата не отрывается от предлога, «вступит» может уйти на свою строку. */
function since(date: string): string {
  return typo(`с ${formatIsoDate(date)}`);
}

/** «с 01.12.2026 — 4 313,94 ₽ без НДС» — для подсказки и узкой строки. */
function upcomingText(upcoming: FleetModelPrcUpcoming): string {
  return `${since(upcoming.valid_from)} — ${formatMoney(upcoming.price)} ₽ без НДС`;
}

/**
 * ПРЦ на сегодня: цена без НДС и «с …»; утверждённая на будущее — «вступит
 * …» (`prc_upcoming`). С НДС и себестоимость — в подсказке и в карточке.
 */
function PrcCell({
  prc,
  upcoming,
}: {
  prc: FleetModelPrcBrief | null;
  upcoming: FleetModelPrcUpcoming | null;
}) {
  if (!prc) {
    if (!upcoming) return <td className="wide-only num c-prc none">не утверждена</td>;
    // Сегодня цены нет, первая утверждённая ещё не вступила — цифра вторым цветом.
    return (
      <td className="wide-only num c-prc future" title={`Вступит ${upcomingText(upcoming)}`}>
        {formatMoney(upcoming.price)}
        <Unit>вступит {since(upcoming.valid_from)}</Unit>
      </td>
    );
  }
  const details = [
    `С НДС: ${formatMoney(prc.price_with_vat)} ₽`,
    `себестоимость ${formatMoney(prc.cost)} ₽`,
    upcoming ? `новая ${upcomingText(upcoming)}` : null,
  ].filter((s): s is string => s !== null);
  return (
    <td className="wide-only num c-prc" title={details.join(' · ')}>
      {formatMoney(prc.price)}
      <Unit>{since(prc.valid_from)}</Unit>
      {prc.project_name && <Unit>{objectOf(prc.project_name)}</Unit>}
      {upcoming && <Unit>вступит новая {since(upcoming.valid_from)}</Unit>}
    </td>
  );
}

/** Чья цена, если не общая: ПРЦ по объектам (миграция 0333). */
function objectOf(name: string): string {
  return typo(`объект «${name}»`);
}

/** Строка ПРЦ в узкой раскладке: сегодня, будущая — или «не утверждена». */
function prcLine(prc: FleetModelPrcBrief | null, upcoming: FleetModelPrcUpcoming | null): string {
  const unit = `${rateUnit('machine_hour')} без НДС`;
  if (prc) {
    const whose = prc.project_name ? `, ${objectOf(prc.project_name)}` : '';
    const now = `ПРЦ ${formatMoney(prc.price)} ${unit}, ${since(prc.valid_from)}${whose}`;
    return upcoming ? `${now}; новая ${upcomingText(upcoming)}` : now;
  }
  if (upcoming) {
    return `ПРЦ ${formatMoney(upcoming.price)} ${unit} вступит ${since(upcoming.valid_from)}`;
  }
  return 'ПРЦ не утверждена';
}

function ariaSort(
  col: Column,
  sort: { key: FleetSortKey; dir: SortDirection } | null,
): 'ascending' | 'descending' | undefined {
  if (!col.sort || sort?.key !== col.sort) return undefined;
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

function ModelRow({
  model,
  flat,
  onOpen,
}: {
  model: FleetModel;
  flat: boolean;
  onOpen: (id: string) => void;
}) {
  const prc = model.prc ?? null;
  const upcoming = model.prc_upcoming ?? null;
  // В группе категория уже в заголовке группы — в строке её не повторяем.
  const meta = [
    flat ? model.category_name : null,
    `${formatCount(model.units_count)} ед.`,
    model.fuel_kind,
  ]
    .filter((s): s is string => Boolean(s))
    .join(' · ');
  const people = [model.owner_name, model.operator_position].filter(Boolean).join(' · ');
  const archived = model.status === 'archived';
  return (
    <tr
      className="row"
      data-archived={archived ? 'true' : undefined}
      onClick={() => {
        // Выделяли текст (скопировать собственника) — это не щелчок по строке.
        if (window.getSelection()?.toString()) return;
        onOpen(model.id);
      }}
    >
      <td className="c-name">
        <NameButton
          type="button"
          data-fleet-open={model.id}
          onClick={(e) => {
            e.stopPropagation();
            onOpen(model.id);
          }}
        >
          {model.name}
        </NameButton>
        <Chips>
          {model.status !== 'active' && <FleetStatusChip status={model.status} />}
          {model.prc_stale && <PrcStaleChip />}
        </Chips>
      </td>
      {flat && (
        <td className={`wide-only${model.category_name ? '' : ' empty'}`}>
          {model.category_name ?? '—'}
        </td>
      )}
      <td className="wide-only num">{formatCount(model.units_count)}</td>
      <td className={`wide-only c-owner${model.owner_name ? '' : ' empty'}`}>
        {model.owner_name ?? '—'}
      </td>
      <td className={`wide-only c-position${model.operator_position ? '' : ' empty'}`}>
        {model.operator_position ?? '—'}
      </td>
      <td className={`wide-only${model.fuel_kind ? '' : ' empty'}`}>{model.fuel_kind ?? '—'}</td>
      <td className={`num c-cost${model.current_cost ? '' : ' empty'}`}>
        {formatMoney(model.current_cost)}
        {model.current_cost && <Unit className="narrow-only">{RUB_NO_VAT}</Unit>}
      </td>
      <PrcCell prc={prc} upcoming={upcoming} />
      <td className="narrow-only c-meta">
        <Meta>{meta}</Meta>
      </td>
      {people && (
        <td className="narrow-only c-people">
          <Meta>{people}</Meta>
        </td>
      )}
      <td className="narrow-only c-prc-line">
        <Meta>{prcLine(prc, upcoming)}</Meta>
      </td>
    </tr>
  );
}

interface ModelTableProps {
  /** Сгруппированный вид — по категориям; `null` — плоский список. */
  groups: readonly FleetModelGroup[] | null;
  models: readonly FleetModel[];
  sort: { key: FleetSortKey; dir: SortDirection } | null;
  onSort: (key: FleetSortKey) => void;
  onOpen: (id: string) => void;
}

export function ModelTable({ groups, models, sort, onSort, onOpen }: ModelTableProps) {
  const flat = groups === null;
  const cols = columns(flat);
  return (
    <ScrollRegion label="Таблица моделей техники">
      <Table>
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={c.num ? 'num' : undefined}
                aria-sort={flat ? ariaSort(c, sort) : undefined}
              >
                {flat && c.sort ? (
                  <SortButton type="button" onClick={() => c.sort && onSort(c.sort)}>
                    {c.label}
                    {sort?.key === c.sort &&
                      (sort.dir === 'asc' ? (
                        <IconArrowUp size={12} />
                      ) : (
                        <IconArrowDown size={12} />
                      ))}
                  </SortButton>
                ) : (
                  c.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        {flat ? (
          <tbody>
            {models.map((m) => (
              <ModelRow key={m.id} model={m} flat onOpen={onOpen} />
            ))}
          </tbody>
        ) : (
          groups.map((g) => (
            <tbody key={g.key}>
              <tr className="group">
                <th colSpan={cols.length} scope="rowgroup">
                  <GroupTitle>{g.title}</GroupTitle>
                  <GroupMeta>
                    {g.models.length}{' '}
                    {pluralizeRu(g.models.length, ['модель', 'модели', 'моделей'])} ·{' '}
                    {formatCount(g.units)} ед.
                  </GroupMeta>
                </th>
              </tr>
              {g.models.map((m) => (
                <ModelRow key={m.id} model={m} flat={false} onOpen={onOpen} />
              ))}
            </tbody>
          ))
        )}
      </Table>
    </ScrollRegion>
  );
}
